import useCommonFun from '@/composables/useCommonFun'
import useFormatDate from '@/composables/useFormatDate'
import i18n from '@/i18n/i18n'
import validateApi from '@/services/validate'
import { useFormStore } from '@/stores/form'
import { useGlobalStore } from '@/stores/global'
import { DateTime } from 'luxon'
import { isValidPhoneNumber } from 'libphonenumber-js'

export const isValidEmail = (value) => {
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,4}))$/
  return emailRegex.test(value?.trim())
}

export const isValidPassword = (value) => {
  const passwordRegex = new RegExp(
    /(?=^.{8,}$)((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/
  )
  return passwordRegex.test(value.trim())
}

export const isEnglishOnly = (value) => {
  const englishOnlyRegex = new RegExp(/^[a-zA-Z ]*$/)
  return englishOnlyRegex.test(value.trim())
}

export const isGpaDecimalValid = (value) => {
  return value >= 3.5 && value < 5
}

export default async function (value, rules) {
  if (!rules || rules?.length === 0) return true
  //#region	required ui
  const requiredUiRule = rules.find((r) => r.name == 'required_ui')

  //#region	required
  const requiredRule = rules.find((r) => r.name == 'required')
  if (requiredRule) {
    const { message, name, ref } = requiredRule
    const isNullOrUndefined = value === null || value === undefined || value === ''
    if (ref) {
      const formStore = useFormStore()
      if (formStore.form.values[ref] && isNullOrUndefined) {
        return i18n.global.t(message)
      }
    }
    // if rule value is religionId:1,2 for example
    if (value != '' && value !== undefined && typeof value != 'boolean') {
      const formStore = useFormStore()
      if (value?.indexOf?.(':') >= 0) {
        const [fieldName, values] = value.split(':')
        const valuesArr = values.split(',').map((v) => +v)

        if (!formStore.form.values[fieldName]) {
          return true
        }

        /**
         * returns if the dependent value is in one the values [1,2]
         * we also need to negate it because returning true means the
         * field is valid.
         */
        if (!valuesArr.includes(formStore.form.values[fieldName])) {
          return true
        } else {
          return false
        }
      }
    }

    if (isNullOrUndefined) {
      if (typeof value == 'number' && value == 0) return true
      return i18n.global.t(message)
    }
  }
  //#endregion

  //#region	required reactive
  const requiredReactiveRule = rules.find((r) => r.name == 'required_reactive')
  if (requiredReactiveRule) {
    const { message, value: ruleConfig } = requiredReactiveRule
    const { operator, value: reactiveValue, ref } = ruleConfig

    let compareAgainst
    if (ref == 'serviceTransactionId') {
      compareAgainst = +useGlobalStore().startedService.serviceTransactionId
    }

    let isValid

    switch (operator) {
      case 'in':
        isValid = reactiveValue.includes(compareAgainst)
        break
      case 'not_in':
      case 'notIn':
        isValid = reactiveValue.includes(compareAgainst) === false
        break
      default:
        isValid = false
        break
    }

    if (isValid && !value) {
      return i18n.global.t(message)
    }
  }
  //#endregion

  //#region	email
  const emailRule = rules.find((r) => r.name == 'email')
  if (emailRule && !isValidEmail(value)) {
    return i18n.global.t(emailRule.message || 'INVALID_EMAIL')
  }
  //#endregion

  //#region	contains
  const containsRule = rules.find((r) => r.name == 'contains')
  if (containsRule && value && !value.trimStart().trimEnd().includes(' ')) {
    return i18n.global.t(containsRule.message)
  }
  //#endregion

  //#region english only text
  const englishOnlyRule = rules.find((r) => r.name == 'englishOnly')
  if (englishOnlyRule && !isEnglishOnly(value)) {
    return i18n.global.t(englishOnlyRule.message)
  }
  //#endregion

  //#region english text
  const englishTextRule = rules.find((r) => r.name == 'englishText')
  if (englishTextRule) {
    const englishTextRegex = new RegExp(/^[a-zA-Z0-9?><;,{}/[\]\-_+.()& |']*$/)
    if (!englishTextRegex.test(value?.trim())) {
      return i18n.global.t(englishTextRule.message)
    }
  }
  //#endregion

  //#region arabic text
  const arabicTextRule = rules.find((r) => r.name == 'arabicText')
  if (arabicTextRule) {
    const arabicTextRegex = new RegExp(
      /^[\u0621-\u064A\u0660-\u0669 ,?0-9, {}/[\]\-_+()&.'ءًٌٍَُُِّْ~-]+$/
    )
    if (value && !arabicTextRegex.test(value?.trim())) {
      return i18n.global.t(arabicTextRule.message)
    }
  }
  //#endregion

  //#region check mobile number
  const uaeMobileRule = rules.find((r) => r.name == 'uae_mobile')
  if (uaeMobileRule && value) {
    // Ensure only leading '00' is replaced
    const formattedValue = value.replace(/^00/, '+')
    return isValidPhoneNumber(formattedValue)
  }
  //#endregion

  //#region check mobile number (intl)
  const intlPhone = rules.find((r) => r.name == 'intl_phone')
  if (intlPhone) {
    const cleaned = value.replace('00', '+')
    return isValidPhoneNumber(cleaned)
  }
  //#endregion

  //#region check home mobile number
  const homeMobileRule = rules.find((r) => r.name == 'home_mobile')
  if (homeMobileRule) {
    let mobileRegex = ''
    if (/^00971/.test(value)) {
      // UAE
      mobileRegex = new RegExp('^(?:\\+971|971|00971)?(?:2|3|4|6|7|9)\\d{7}$')
    } else {
      mobileRegex = new RegExp('^(.|[\\r\\n]){14,}$')
    }
    if (!mobileRegex.test(value?.trim())) {
      return i18n.global.t(homeMobileRule.message || 'inValidHomePhone')
    }
    return true
  }
  //#endregion

  //#region check validate user
  const validateUserNameRule = rules.find((r) => r.name == 'validateUserName')
  if (validateUserNameRule) {
    if (value?.length < 8 || !isValidEmail(value)) {
      return i18n.global.t(validateUserNameRule.message)
    } else {
      try {
        const res = await validateApi.validateUserName(value)

        if (res == 1) {
          return i18n.global.t('EMAIL_ALREADY_USED')
        } else if (res == 4) {
          return i18n.global.t('inactiveAccount')
        } else {
          return true
        }
      } catch (error) {
        return i18n.global.t(validateUserNameRule.message)
      }
    }
  }
  //#endregion

  //#region check visa birth date is larger than 18
  const visaBirthDateRule = rules.find((r) => r.name == 'visa_date_larger_18')
  if (visaBirthDateRule) {
    try {
      const res = await validateApi.validateVisaBirthday(value)
      if (res.dateOfBirth) {
        if (useCommonFun().ageComparison(res.dateOfBirth, 18, '<')) {
          return i18n.global.t(visaBirthDateRule.message)
        }
      } else {
        return i18n.global.t(visaBirthDateRule.message)
      }
    } catch (error) {
      return i18n.global.t(visaBirthDateRule.message)
    }
  }
  //#endregion

  //#region check visa birth date is larger than 18
  const Larger18BirthDateRule = rules.find((r) => r.name == 'date_larger_18')
  if (Larger18BirthDateRule) {
    try {
      if (value) {
        if (useCommonFun().ageComparison(value, 18, '<')) {
          return i18n.global.t(Larger18BirthDateRule.message)
        }
      } else {
        return i18n.global.t(Larger18BirthDateRule.message)
      }
    } catch (error) {
      return i18n.global.t(Larger18BirthDateRule.message)
    }
  }
  //#endregion
  //#region check birth date is less than 18
  const Less18BirthDateRule = rules.find((r) => r.name == 'date_less_18')
  if (Less18BirthDateRule) {
    try {
      if (value) {
        if (useCommonFun().ageComparison(value, 18, '>')) {
          return i18n.global.t(Less18BirthDateRule.message)
        }
      } else {
        return i18n.global.t(Less18BirthDateRule.message)
      }
    } catch (error) {
      return i18n.global.t(Less18BirthDateRule.message)
    }
  }
  //#endregion

  //#region check password
  const passwordRule = rules.find((r) => r.name == 'password')
  if (passwordRule && !isValidPassword(value.trim())) {
    return i18n.global.t(passwordRule.message)
  }
  //#endregion

  //#region check same value such as password and confirm password
  const sameValueRule = rules.find((r) => r.name == 'sameValue')
  if (sameValueRule) {
    const formStore = useFormStore()
    if (value !== formStore?.form?.values?.[sameValueRule.value]) {
      return i18n.global.t(sameValueRule.message)
    }
  }
  //#endregion

  //#region check max length
  const maxLengthRule = rules.find((r) => r.name == 'max_char')
  if (maxLengthRule) {
    if ((value || '').length > maxLengthRule.value) {
      return i18n.global.t(maxLengthRule.message, [maxLengthRule.value])
    }
  }
  //#endregion
  //#region check min length
  const minLengthRule = rules.find((r) => r.name == 'min_char')
  if (minLengthRule) {
    // no need to check if the value is null or undefined
    if (!value) {
      return true
    }
    if ((value || '').length < minLengthRule.value) {
      return i18n.global.t('INVALID_MIN_CHAR', [minLengthRule.value])
    }
  }
  //#endregion

  //#region check min date
  const minDateRule = rules.find((r) => r.name == 'min_date')
  if (minDateRule) {
    const formStore = useFormStore()
    const hasMinDate = value && formStore?.form?.values?.[minDateRule.ref]
    if (minDateRule.ref) {
      if (
        hasMinDate &&
        useFormatDate().formateDateFromDDMMYYYY(value) <
          useFormatDate().formateDateFromDDMMYYYY(formStore?.form?.values?.[minDateRule.ref])
      ) {
        return i18n.global.t(minDateRule.message)
      }
    } else {
      if (
        hasMinDate &&
        useFormatDate().formateDateFromDDMMYYYY(value) <
          useFormatDate().formateDateFromDDMMYYYY(minDateRule.value)
      ) {
        return i18n.global.t(minDateRule.message)
      }
    }
  }
  //#endregion

  //#region check max date
  const maxDateRule = rules.find((r) => r.name == 'max_date')
  if (maxDateRule) {
    if (maxDateRule.ref) {
      const formStore = useFormStore()
      const hasMaxDate = value && formStore?.form?.values?.[maxDateRule.ref]
      if (
        hasMaxDate &&
        useFormatDate().formateDateFromDDMMYYYY(value) >
          useFormatDate().formateDateFromDDMMYYYY(formStore?.form?.values?.[maxDateRule.ref])
      ) {
        return i18n.global.t(maxDateRule.message)
      }
    } else {
      if (
        value &&
        useFormatDate().formateDateFromDDMMYYYY(value) > maxDateRule.value &&
        useFormatDate().formateDateFromDDMMYYYY(maxDateRule.value)
      ) {
        return i18n.global.t(maxDateRule.message)
      }
    }
  }
  //#endregion

  //#region check passport number
  const passportRule = rules.find((r) => r.name === 'passport')
  if (passportRule) {
    const passportRegex = /^[a-zA-Z0-9]+$/ // Only alphanumeric, whole string
    if (value?.trim() && !passportRegex.test(value.trim())) {
      return i18n.global.t(passportRule.message || 'Invalid passport format')
    }
  }
  //#endregion

  //#region checkAddressText
  const addressTextRule = rules.find((r) => r.name == 'addressText')
  if (addressTextRule) {
    if (i18n.global.locale.value == 'ar') {
      const arabicTextRegex = new RegExp(
        /^[\u0621-\u064A\u0660-\u0669 ,?0-9, {}/[\]\-_+()&.'ءًٌٍَُُِّْ~-]+$/
      )
      if (!arabicTextRegex.test(value.trim())) {
        return i18n.global.t('ArabicContentOnly')
      }
    } else {
      const englishTextRegex = new RegExp(/^[a-zA-Z0-9?><;,{}/[\]\-_+.()& |']*$/)
      if (!englishTextRegex.test(value.trim())) {
        return i18n.global.t('EnglishContentOnly')
      }
    }
  }
  //#endregion

  //#region number only
  const numberOnlyRule = rules.find((r) => r.name == 'numberOnly')
  if (numberOnlyRule) {
    const numberOnlyRegex = new RegExp(/^[0-9]*$/)
    const trimmedValue = ('' + value)?.trim()
    // Bypass the regex test if the value is null or an empty string
    if (!trimmedValue && !numberOnlyRegex.test(trimmedValue)) {
      return i18n.global.t(numberOnlyRule.message)
    }
  }
  //#endregion

  //#region greater_than_Zero
  const greaterThanZeroRule = rules.find((r) => r.name == 'greaterThanZero')
  if (greaterThanZeroRule) {
    const numberOnlyRegex = new RegExp(/^[1-9]\d*$/)
    if (value && !numberOnlyRegex.test(('' + value)?.trim())) {
      return i18n.global.t(greaterThanZeroRule.message)
    }
  }
  //#endregion

  //#region religionId_check
  const religionIdRule = rules.find((r) => r.name == 'religionId_check')
  if (religionIdRule) {
    if (religionIdRule.ref) {
      const formStore = useFormStore()
      if (
        formStore?.form?.values?.[religionIdRule.ref] &&
        formStore?.form?.values?.[religionIdRule.ref] != 11 &&
        !value
      ) {
        return i18n.global.t(religionIdRule.message)
      }
    }
  }
  //#endregion

  //#region graduation date must be less than two years
  const checkGraduationDateTwoYears = rules.find((r) => r.name == 'checkGraduationDateTwoYears')
  if (checkGraduationDateTwoYears) {
    const dateLxn = useFormatDate().formateDateFromDDMMYYYY(value)
    const twoYa = DateTime.now().minus({ years: 2 })

    if (dateLxn <= twoYa) {
      return i18n.global.t(checkGraduationDateTwoYears.message)
    }
  }
  //#endregion

  //#region GPA Percentage
  const checkGPAPerc = rules.find((r) => r.name == 'checkGPAPerc')
  if (checkGPAPerc) {
    // we know from the mask that there will be a '%'
    const valueAsNum = +value.replaceAll('%', '')
    if (valueAsNum < 95 || valueAsNum >= 100) {
      return i18n.global.t(checkGPAPerc.message)
    }
  }
  //#endregion

  //#region GPA Decimal
  const checkGPADecimal = rules.find((r) => r.name == 'checkGPADecimal')
  if (checkGPADecimal) {
    if (!isGpaDecimalValid(+value)) {
      return i18n.global.t(checkGPADecimal.message)
    }
  }
  //#endregion

  //#region alpha_num
  const alphaNumRule = rules.find((r) => r.name == 'alpha_num')
  if (alphaNumRule) {
    const alphaNumRegex = new RegExp(
      /^([a-z?-?������a��c����e�����ln�������s�������zz��dd0-9])+$/,
      'i'
    )
    if (!alphaNumRegex.test(value.trim())) {
      return i18n.global.t(alphaNumRule.message)
    }
  }
  //#endregion

  //#region	is_numeric
  const isNumeric = rules.find((r) => r.name == 'is_numeric')
  if (isNumeric) {
    if (isNaN(value.toString().replaceAll(',', '') / 1)) {
      return i18n.global.t(isNumeric.message)
    }
  }
  //#endregion

  //#region gte
  const gte = rules.find((r) => r.name == 'greaterThan')
  if (gte && value) {
    const parsed = Number.parseFloat(String(value).replaceAll(',', ''))
    if (gte.value <= parsed) {
      return true
    }

    return i18n.global.t(gte.message)
  }
  //#endregion

  //#region passport expiry
  const oneMoFromNow = rules.find((r) => r.name == '1moFromNow')
  if (oneMoFromNow) {
    const dateLxn = useFormatDate().formateDateFromDDMMYYYY(value)
    const oneMonthLater = DateTime.now().plus({ month: 1 })

    if (dateLxn < oneMonthLater) {
      return i18n.global.t(oneMoFromNow.message)
    }
  }

  const sixMoFromNow = rules.find((r) => r.name == '6moFromNow')
  if (sixMoFromNow && value) {
    const dateLxn = useFormatDate().formateDateFromDDMMYYYY(value)
    const sixMonthsLater = DateTime.now().plus({ month: 6 })

    if (dateLxn < sixMonthsLater) {
      return i18n.global.t(sixMoFromNow.message)
    }
  }
  //#endregion

  return true
}
